import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Mail, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { extractErr } from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Provider = 'MS_GRAPH' | 'SMTP'

type EmailSettings = {
  provider: Provider
  // Microsoft Graph
  ms_tenant_id: string
  ms_client_id: string
  ms_client_secret_set: boolean
  // SMTP
  host: string
  port: number
  use_tls: boolean
  use_ssl: boolean
  username: string
  password_set: boolean
  // Shared
  from_address: string
  from_name: string
  // Invitation templates
  invite_subject_template: string
  invite_body_text_template: string
  invite_body_html_template: string
  invite_from_name_override: string
  is_active: boolean
  is_configured: boolean
  last_test_at: string | null
  last_test_ok: boolean
  last_test_error: string
}

type PreviewResponse = {
  subject: string
  body_text: string
  body_html: string
  effective_from_name: string
  sample_to: string
}

type TestResponse = {
  ok: boolean
  error: string
  sent_to: string
  last_test_at: string
}

const SMTP_PRESETS = {
  gmail: {
    label: 'Gmail / Google Workspace',
    host: 'smtp.gmail.com',
    port: 587,
    use_tls: true,
    use_ssl: false,
  },
  sendgrid: {
    label: 'SendGrid',
    host: 'smtp.sendgrid.net',
    port: 587,
    use_tls: true,
    use_ssl: false,
  },
  postmark: {
    label: 'Postmark',
    host: 'smtp.postmarkapp.com',
    port: 587,
    use_tls: true,
    use_ssl: false,
  },
}

export default function AdminEmailSettings() {
  const qc = useQueryClient()
  const settingsQ = useQuery({
    queryKey: ['core', 'email-settings'],
    queryFn: async () => (await api.get<EmailSettings>('/email-settings/')).data,
  })

  // Local form state.
  const [draft, setDraft] = useState<{
    provider: Provider
    ms_tenant_id: string; ms_client_id: string; ms_client_secret: string
    host: string; port: number; use_tls: boolean; use_ssl: boolean
    username: string; password: string
    from_address: string; from_name: string
    invite_subject_template: string
    invite_body_text_template: string
    invite_body_html_template: string
    invite_from_name_override: string
    is_active: boolean
  }>({
    provider: 'MS_GRAPH',
    ms_tenant_id: '', ms_client_id: '', ms_client_secret: '',
    host: '', port: 587, use_tls: true, use_ssl: false,
    username: '', password: '',
    from_address: '', from_name: 'MooderERP',
    invite_subject_template: '', invite_body_text_template: '',
    invite_body_html_template: '', invite_from_name_override: '',
    is_active: false,
  })
  const [pwTouched, setPwTouched] = useState(false)
  const [csTouched, setCsTouched] = useState(false)

  useEffect(() => {
    if (settingsQ.data) {
      const s = settingsQ.data
      setDraft({
        provider: s.provider,
        ms_tenant_id: s.ms_tenant_id, ms_client_id: s.ms_client_id, ms_client_secret: '',
        host: s.host, port: s.port, use_tls: s.use_tls, use_ssl: s.use_ssl,
        username: s.username, password: '',
        from_address: s.from_address, from_name: s.from_name,
        invite_subject_template: s.invite_subject_template,
        invite_body_text_template: s.invite_body_text_template,
        invite_body_html_template: s.invite_body_html_template,
        invite_from_name_override: s.invite_from_name_override,
        is_active: s.is_active,
      })
      setPwTouched(false); setCsTouched(false)
    }
  }, [settingsQ.data])

  function buildPayload() {
    const payload: any = {
      provider: draft.provider,
      from_address: draft.from_address.trim(),
      from_name: draft.from_name.trim(),
      // Invite templates — always sent (blank string means "use default")
      invite_subject_template: draft.invite_subject_template,
      invite_body_text_template: draft.invite_body_text_template,
      invite_body_html_template: draft.invite_body_html_template,
      invite_from_name_override: draft.invite_from_name_override.trim(),
      is_active: draft.is_active,
    }
    if (draft.provider === 'MS_GRAPH') {
      payload.ms_tenant_id = draft.ms_tenant_id.trim()
      payload.ms_client_id = draft.ms_client_id.trim()
      if (csTouched && draft.ms_client_secret) {
        payload.ms_client_secret = draft.ms_client_secret
      }
    } else {
      payload.host = draft.host.trim()
      payload.port = draft.port
      payload.use_tls = draft.use_tls
      payload.use_ssl = draft.use_ssl
      payload.username = draft.username.trim()
      if (pwTouched && draft.password) {
        payload.password = draft.password
      }
    }
    return payload
  }

  const saveMut = useMutation({
    mutationFn: async () => (await api.put<EmailSettings>('/email-settings/', buildPayload())).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['core', 'email-settings'] })
      toast.success('Settings saved')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const testMut = useMutation({
    mutationFn: async () =>
      (await api.post<TestResponse>('/email-settings/test/', buildPayload())).data,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['core', 'email-settings'] })
      if (res.ok) toast.success(`Test email sent to ${res.sent_to}`)
      else toast.error(`Test failed: ${res.error}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  function applySmtpPreset(key: keyof typeof SMTP_PRESETS) {
    const p = SMTP_PRESETS[key]
    setDraft((d) => ({
      ...d,
      provider: 'SMTP',
      host: p.host, port: p.port, use_tls: p.use_tls, use_ssl: p.use_ssl,
    }))
    toast.success(`${p.label} preset applied`)
  }

  const cur = settingsQ.data
  const isGraph = draft.provider === 'MS_GRAPH'

  return (
    <AdminLayout>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Email Settings</h2>
          <p className="text-sm text-muted-foreground">
            Outgoing email for invites + future transactional notifications.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => testMut.mutate()} disabled={testMut.isPending}>
            <Mail className="h-3 w-3 mr-1" />
            {testMut.isPending ? 'Sending…' : 'Send test email'}
          </Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
            {saveMut.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Status banner */}
      {cur && (() => {
        const noConfig = !cur.is_configured
        const testedOk = cur.last_test_ok && cur.last_test_at
        const live = cur.is_configured && testedOk && cur.is_active
        const tone = live ? 'border-green-200 bg-green-50 text-green-900'
          : noConfig ? 'border-gray-200 bg-gray-50 text-gray-700'
          : 'border-amber-200 bg-amber-50 text-amber-900'
        return (
          <div className={`mb-4 rounded border px-4 py-3 text-sm flex items-start gap-2 ${tone}`}>
            {live ? <CheckCircle2 className="h-4 w-4 mt-0.5" /> : <AlertCircle className="h-4 w-4 mt-0.5" />}
            <div className="flex-1">
              {noConfig && (
                <div>
                  <strong>Not configured yet.</strong> Fill in the {isGraph ? 'Microsoft Graph' : 'SMTP'} credentials below, then click <em>Send test email</em>.
                </div>
              )}
              {!noConfig && !testedOk && !cur.last_test_at && (
                <div>
                  <strong>Ready to test.</strong> Click <em>Send test email</em> — no need to flip the master switch yet.
                </div>
              )}
              {!noConfig && cur.last_test_at && !cur.last_test_ok && (
                <div>
                  <strong>Last test failed</strong> at {new Date(cur.last_test_at).toLocaleString()}:
                  <div className="mt-1 font-mono text-xs whitespace-pre-wrap">{cur.last_test_error}</div>
                </div>
              )}
              {!noConfig && testedOk && !cur.is_active && (
                <div>
                  <strong>Test succeeded</strong> {cur.last_test_at && new Date(cur.last_test_at).toLocaleString()}.
                  Turn on the master switch below to start auto-sending invite emails.
                </div>
              )}
              {live && (
                <div>
                  <strong>Email is live.</strong> Last test succeeded {cur.last_test_at && new Date(cur.last_test_at).toLocaleString()}.
                </div>
              )}
            </div>
          </div>
        )
      })()}

      {/* Provider selector */}
      <div className="mb-4 bg-card rounded-lg shadow p-4 max-w-3xl">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Provider
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setDraft({ ...draft, provider: 'MS_GRAPH' })}
            className={`text-left px-4 py-3 rounded border-2 ${
              isGraph
                ? 'border-violet-500 bg-violet-50'
                : 'border-border hover:border-violet-300'
            }`}
          >
            <div className="font-semibold text-sm">Microsoft 365 (Graph API + OAuth)</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Recommended for any M365 tenant. Requires an Azure app registration.
            </div>
          </button>
          <button
            type="button"
            onClick={() => setDraft({ ...draft, provider: 'SMTP' })}
            className={`text-left px-4 py-3 rounded border-2 ${
              !isGraph
                ? 'border-violet-500 bg-violet-50'
                : 'border-border hover:border-violet-300'
            }`}
          >
            <div className="font-semibold text-sm">SMTP</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              For Gmail / Google Workspace, SendGrid, Postmark, or any generic SMTP relay.
            </div>
          </button>
        </div>
      </div>

      {/* Provider-specific fields */}
      <div className="bg-card rounded-lg shadow p-6 max-w-3xl">
        {isGraph ? <GraphFields draft={draft} setDraft={setDraft} cur={cur} csTouched={csTouched} setCsTouched={setCsTouched} /> : <SmtpFields draft={draft} setDraft={setDraft} cur={cur} pwTouched={pwTouched} setPwTouched={setPwTouched} applyPreset={applySmtpPreset} />}

        {/* Shared identity */}
        <FieldGroup className="mt-4 pt-4 border-t">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="em-from">From address *</FieldLabel>
              <Input id="em-from" type="email" value={draft.from_address}
                onChange={(e) => setDraft({ ...draft, from_address: e.target.value })}
                placeholder="noreply@yourdomain.com" />
              <FieldDescription>
                {isGraph
                  ? 'The mailbox the Azure app is authorized to send as. Usually the user the ApplicationAccessPolicy is scoped to.'
                  : 'Must be allowed to send through this SMTP server.'}
              </FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="em-fromname">From name</FieldLabel>
              <Input id="em-fromname" value={draft.from_name}
                onChange={(e) => setDraft({ ...draft, from_name: e.target.value })}
                placeholder="MooderERP" />
            </Field>
          </div>

          <Field>
            <label className="text-sm flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={draft.is_active}
                onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} />
              <strong>Master switch — outgoing email is active</strong>
            </label>
            <FieldDescription>
              Turn this on only after the test email succeeds. While off, invite links are still copy-pasteable.
            </FieldDescription>
          </Field>
        </FieldGroup>
      </div>

      {/* Invitation email template editor */}
      <InviteTemplateEditor draft={draft} setDraft={setDraft} />
    </AdminLayout>
  )
}

// ─────────────────── Graph (OAuth) fields ───────────────────

function GraphFields({
  draft, setDraft, cur, csTouched, setCsTouched,
}: any) {
  return (
    <FieldGroup>
      <div className="rounded border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-900 space-y-1.5 mb-3">
        <div className="font-semibold">Azure setup checklist:</div>
        <div>1. Azure portal → Microsoft Entra ID → <strong>App registrations → New registration</strong> (single tenant, no redirect URI)</div>
        <div>2. Copy the <strong>Application (client) ID</strong> + <strong>Directory (tenant) ID</strong></div>
        <div>3. <strong>Certificates & secrets → New client secret</strong> → copy the <strong>Value</strong> (only shown once)</div>
        <div>4. <strong>API permissions → Add permission → Microsoft Graph → Application permissions → Mail.Send → Grant admin consent</strong></div>
        <div>5. <em>(recommended)</em> Lock the app to one mailbox with PowerShell <code>New-ApplicationAccessPolicy</code></div>
        <a
          href="https://learn.microsoft.com/en-us/graph/auth-v2-service"
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-blue-700 hover:underline mt-1"
        >
          Microsoft Graph client-credentials docs <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      <Field>
        <FieldLabel htmlFor="em-tenant">Directory (tenant) ID *</FieldLabel>
        <Input id="em-tenant" value={draft.ms_tenant_id}
          onChange={(e) => setDraft({ ...draft, ms_tenant_id: e.target.value })}
          placeholder="00000000-0000-0000-0000-000000000000" />
        <FieldDescription>UUID of your Azure AD tenant. Found in the app registration overview.</FieldDescription>
      </Field>

      <Field>
        <FieldLabel htmlFor="em-client">Application (client) ID *</FieldLabel>
        <Input id="em-client" value={draft.ms_client_id}
          onChange={(e) => setDraft({ ...draft, ms_client_id: e.target.value })}
          placeholder="00000000-0000-0000-0000-000000000000" />
      </Field>

      <Field>
        <FieldLabel htmlFor="em-secret">Client secret {cur?.ms_client_secret_set ? '(set)' : '*'}</FieldLabel>
        <Input
          id="em-secret"
          type="password"
          value={draft.ms_client_secret}
          onChange={(e) => { setDraft({ ...draft, ms_client_secret: e.target.value }); setCsTouched(true) }}
          placeholder={cur?.ms_client_secret_set ? '••••••••  (leave blank to keep)' : 'The "Value" you copied from Certificates & secrets'}
        />
        <FieldDescription>
          Stored as plain text — rotate when Azure says it's about to expire (every 12-24 months).
        </FieldDescription>
      </Field>
    </FieldGroup>
  )
}

// ─────────────────── SMTP fields ───────────────────

function SmtpFields({
  draft, setDraft, cur, pwTouched, setPwTouched, applyPreset,
}: any) {
  return (
    <FieldGroup>
      <div className="flex flex-wrap items-center gap-2 text-xs mb-2">
        <span className="text-muted-foreground">Preset:</span>
        {(Object.keys(SMTP_PRESETS) as (keyof typeof SMTP_PRESETS)[]).map((key) => (
          <button
            key={key} type="button"
            onClick={() => applyPreset(key)}
            className="px-2.5 py-1 rounded border border-border bg-card hover:bg-muted text-foreground"
          >
            {SMTP_PRESETS[key].label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4">
        <Field>
          <FieldLabel htmlFor="em-host">SMTP host *</FieldLabel>
          <Input id="em-host" value={draft.host}
            onChange={(e) => setDraft({ ...draft, host: e.target.value })}
            placeholder="smtp.gmail.com" />
        </Field>
        <Field>
          <FieldLabel htmlFor="em-port">Port</FieldLabel>
          <Input id="em-port" type="number" value={draft.port}
            onChange={(e) => setDraft({ ...draft, port: Number(e.target.value) || 587 })} />
          <FieldDescription>587 for TLS · 465 for SSL · 25 plain (not recommended)</FieldDescription>
        </Field>
      </div>

      <div className="flex items-center gap-6">
        <label className="text-sm flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={draft.use_tls}
            onChange={(e) => setDraft({ ...draft, use_tls: e.target.checked, use_ssl: e.target.checked ? false : draft.use_ssl })} />
          Use STARTTLS (port 587)
        </label>
        <label className="text-sm flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={draft.use_ssl}
            onChange={(e) => setDraft({ ...draft, use_ssl: e.target.checked, use_tls: e.target.checked ? false : draft.use_tls })} />
          Use SSL (port 465)
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field>
          <FieldLabel htmlFor="em-user">Username *</FieldLabel>
          <Input id="em-user" value={draft.username}
            onChange={(e) => setDraft({ ...draft, username: e.target.value })}
            placeholder="noreply@yourdomain.com" />
        </Field>
        <Field>
          <FieldLabel htmlFor="em-pw">Password {cur?.password_set ? '(set)' : '*'}</FieldLabel>
          <Input id="em-pw" type="password" value={draft.password}
            onChange={(e) => { setDraft({ ...draft, password: e.target.value }); setPwTouched(true) }}
            placeholder={cur?.password_set ? '••••••••  (leave blank to keep)' : 'SMTP password / app password'} />
        </Field>
      </div>
    </FieldGroup>
  )
}

// ─────────────────── Invite template editor ───────────────────

const PLACEHOLDERS: { token: string; meaning: string }[] = [
  { token: '{org_name}',         meaning: 'Your organization name (from Organization → Group name)' },
  { token: '{role}',             meaning: 'The role this person is being invited as' },
  { token: '{role_description}', meaning: 'The role\'s description (from Roles admin)' },
  { token: '{accept_link}',      meaning: 'Full URL the recipient clicks to accept' },
  { token: '{message}',          meaning: 'Personal note attached to the invite (raw text)' },
  { token: '{message_block}',    meaning: 'Personal note auto-formatted with a prefix line; blank when no note' },
  { token: '{message_block_html}', meaning: 'Same as above but as a styled HTML blockquote — use inside HTML body' },
  { token: '{recipient_email}',  meaning: 'Email the invite was sent to' },
  { token: '{expiry_days}',      meaning: 'Number of days the link is valid' },
]

function InviteTemplateEditor({
  draft, setDraft,
}: {
  draft: any
  setDraft: (d: any) => void
}) {
  // Build a debounced preview so the iframe doesn't refetch on every keystroke.
  const [debounced, setDebounced] = useState({
    subject: draft.invite_subject_template,
    text: draft.invite_body_text_template,
    html: draft.invite_body_html_template,
  })
  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced({
        subject: draft.invite_subject_template,
        text: draft.invite_body_text_template,
        html: draft.invite_body_html_template,
      })
    }, 400)
    return () => clearTimeout(id)
  }, [draft.invite_subject_template, draft.invite_body_text_template, draft.invite_body_html_template])

  const previewQ = useQuery<PreviewResponse>({
    queryKey: ['core', 'email-settings', 'preview', debounced.subject, debounced.text, debounced.html],
    queryFn: async () =>
      (await api.post<PreviewResponse>('/email-settings/preview-invite/', {
        invite_subject_template: debounced.subject,
        invite_body_text_template: debounced.text,
        invite_body_html_template: debounced.html,
      })).data,
  })

  return (
    <div className="mt-6 bg-card rounded-lg shadow p-6 max-w-5xl">
      <h3 className="text-lg font-bold text-foreground mb-1">Invitation email</h3>
      <p className="text-xs text-muted-foreground mb-4">
        Subject + body sent to people you invite. Leave a field blank to use the built-in default
        (which uses your <strong>Organization → Group name</strong> automatically).
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: editors */}
        <div className="space-y-4">
          <Field>
            <FieldLabel htmlFor="inv-fromname">From-name override</FieldLabel>
            <Input
              id="inv-fromname"
              value={draft.invite_from_name_override}
              onChange={(e) => setDraft({ ...draft, invite_from_name_override: e.target.value })}
              placeholder='e.g. "Oben Recruiting" — blank to use the global From name'
            />
            <FieldDescription>
              Shown in the recipient's inbox as the sender's name. The address itself stays as the From address above.
            </FieldDescription>
          </Field>

          <Field>
            <FieldLabel htmlFor="inv-subj">Subject</FieldLabel>
            <Input
              id="inv-subj"
              value={draft.invite_subject_template}
              onChange={(e) => setDraft({ ...draft, invite_subject_template: e.target.value })}
              placeholder="You're invited to join {org_name} as {role}"
            />
            <div className="flex justify-end mt-1">
              <button
                type="button"
                className="text-xs text-violet-600 hover:underline"
                onClick={() => setDraft({ ...draft, invite_subject_template: '' })}
              >
                Reset to default
              </button>
            </div>
          </Field>

          <Field>
            <FieldLabel htmlFor="inv-text">Plain-text body</FieldLabel>
            <textarea
              id="inv-text"
              rows={10}
              value={draft.invite_body_text_template}
              onChange={(e) => setDraft({ ...draft, invite_body_text_template: e.target.value })}
              className="w-full rounded-lg border border-input bg-transparent px-2.5 py-1.5 text-sm font-mono outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
              placeholder="Hi,&#10;&#10;You've been invited to join {org_name} as {role}.&#10;..."
            />
            <div className="flex justify-end mt-1">
              <button
                type="button"
                className="text-xs text-violet-600 hover:underline"
                onClick={() => setDraft({ ...draft, invite_body_text_template: '' })}
              >
                Reset to default
              </button>
            </div>
            <FieldDescription>
              Fallback for mail clients that don't render HTML. Most do, but accessibility tools rely on this.
            </FieldDescription>
          </Field>

          <Field>
            <FieldLabel htmlFor="inv-html">HTML body</FieldLabel>
            <textarea
              id="inv-html"
              rows={10}
              value={draft.invite_body_html_template}
              onChange={(e) => setDraft({ ...draft, invite_body_html_template: e.target.value })}
              className="w-full rounded-lg border border-input bg-transparent px-2.5 py-1.5 text-sm font-mono outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
              placeholder="<html><body>...</body></html>"
            />
            <div className="flex justify-end mt-1">
              <button
                type="button"
                className="text-xs text-violet-600 hover:underline"
                onClick={() => setDraft({ ...draft, invite_body_html_template: '' })}
              >
                Reset to default
              </button>
            </div>
            <FieldDescription>
              Inline CSS only — most email clients strip {'<style>'} blocks and external links.
            </FieldDescription>
          </Field>
        </div>

        {/* Right: preview + placeholder reference */}
        <div className="space-y-4">
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Live preview
            </div>
            <div className="rounded border border-border bg-muted/20 p-3 text-xs">
              <div>
                <span className="text-muted-foreground">To:</span>{' '}
                <span className="font-mono">{previewQ.data?.sample_to || 'alex.example@email.com'}</span>
              </div>
              <div className="mt-0.5">
                <span className="text-muted-foreground">From name:</span>{' '}
                <strong>{previewQ.data?.effective_from_name || '—'}</strong>
              </div>
              <div className="mt-1 text-sm font-semibold border-t border-border pt-2">
                {previewQ.data?.subject || <span className="text-muted-foreground">rendering…</span>}
              </div>
            </div>
            <div
              className="mt-1 rounded border border-border bg-card overflow-auto"
              style={{ maxHeight: '420px' }}
            >
              {previewQ.data?.body_html ? (
                <iframe
                  title="invite-html-preview"
                  srcDoc={previewQ.data.body_html}
                  className="w-full"
                  style={{ minHeight: '380px', border: 'none' }}
                />
              ) : (
                <div className="p-4 text-xs text-muted-foreground">Rendering preview…</div>
              )}
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Preview uses a sample invite (role: Recruiter, inviter: you). The real send substitutes real values.
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Available placeholders
            </div>
            <table className="w-full text-xs">
              <tbody className="divide-y divide-border">
                {PLACEHOLDERS.map((p) => (
                  <tr key={p.token}>
                    <td className="py-1 pr-2 align-top">
                      <code className="font-mono text-violet-700 bg-violet-50 px-1 rounded">{p.token}</code>
                    </td>
                    <td className="py-1 text-muted-foreground">{p.meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
